import React, { useState, useMemo } from 'react'

import { LinkOutlined, PlusOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import ListPageHeader from '@shared/ListPageHeader'
import TableIndex from '@shared/table/TableIndex'

import { warehouseService } from '@services/operator/warehouseService'
import { formatDateTime } from '../../../utils/dateTime'

interface WarehouseRow {
  id: string;
  name?: string;
  address?: string | null;
  postal_code?: string | null;
  full_address?: string | null;
  phone?: string | null;
  created_at?: string;
  [key: string]: unknown;
}

export default function WarehousesList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [lastModified, setLastModified] = useState(0)

  const columns = useMemo(() => [
    {
      nameKey: `models.warehouse.name`,
      persist: true,
      sortable: true,
      sortFieldName: 'warehouses.name',
      width: '200px',
      wrap: true,
      cell: ({ id, name }: WarehouseRow) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Link to={`/warehouses/${id}`} style={{ wordBreak: 'break-word' }}>
            {name}
          </Link>
          <a
            href={`/operator/warehouses/${id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }} rel="noreferrer"
          >
            <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
          </a>
        </div>
      ),
    },
    {
      nameKey: `models.warehouse.address`,
      width: '250px',
      wrap: true,
      cell: ({ full_address }: WarehouseRow) => full_address || '-',
    },
    {
      nameKey: `models.warehouse.phone`,
      width: '150px',
      cell: ({ phone }: WarehouseRow) => phone || '-',
    },
    {
      nameKey: `common.created_at`,
      sortable: true,
      sortFieldName: 'warehouses.created_at',
      width: '180px',
      wrap: true,
      cell: ({ created_at }: WarehouseRow) => created_at ? formatDateTime(created_at) : '-',
    },
  ], [t])

  return (
    <div>
      <ListPageHeader
        items={['warehouse_settings', 'warehouse_settings.warehouses']}
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/warehouses/new')}
          >
            {t('common.button.new')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        searchable={{
          match: 'contains',
          searchFields: [
            t('models.warehouse.name'),
            t('models.warehouse.address'),
            t('models.warehouse.phone'),
          ],
        }}
        columnsEditable={true}
        tableId="OperatorWarehousesTable"
        defaultQuery={{
          sorting_field: 'warehouses.id',
          sorting_direction: 'desc',
        }}
        supportRules={[]}
        lastModified={lastModified}
        setLastModified={setLastModified}
        actions={[]}
        service={warehouseService}
      />
    </div>
  )
}
